import { useMemo } from 'react';
import {
  ClientProviderStatus,
  useOpenFeatureClientStatus,
  useWhenProviderReady,
} from '@openfeature/react-sdk';
import type { FlagKey, FlagValue } from '../types';
import { getFlag } from './get-flag';

interface UseFlagConditionallyReturn<K extends FlagKey> {
  value: FlagValue<K>;
  isReady: boolean;
}

export function useFlagConditionally<K extends FlagKey>(
  condition: boolean,
  key: K,
  defaultFlagValue: FlagValue<K>,
  { suspend }: { suspend: boolean } = { suspend: false },
): UseFlagConditionallyReturn<K> {
  useWhenProviderReady({ suspendUntilReady: suspend });
  const status = useOpenFeatureClientStatus();

  return useMemo(() => {
    if (!condition) {
      return {
        value: defaultFlagValue,
        isReady: false,
      };
    }

    if (status === ClientProviderStatus.READY) {
      const { value } = getFlag(key, defaultFlagValue);

      return {
        value,
        isReady: true,
      };
    }

    if (
      status === ClientProviderStatus.ERROR ||
      status === ClientProviderStatus.FATAL
    ) {
      return {
        value: defaultFlagValue,
        isReady: false,
      };
    }

    return {
      value: defaultFlagValue,
      isReady: false,
    };
  }, [condition, defaultFlagValue, key, status]);
}
